import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {ShieldCheckIcon, UserIcon} from '@heroicons/react/24/outline';
import {IApiKey, createApiKey, VerdocsEndpoint} from '@verdocs/js-sdk';
import {AccessCard, Button, SelectInput, Spinner, TextInput} from '../Components';
import {useOrganizationMembers} from '../react-sdk/queries';
import {AppState} from '../AppState';
import {Dialog} from './Dialog';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  profile_id: z.string().min(1, 'Please select an owner'),
  global_admin: z.boolean(),
});

type SchemaType = z.infer<typeof schema>;

interface ApiKeyDialogProps {
  onClose: () => void;
  onCreated: (key: IApiKey) => void;
}

export const CreateApiKeyDialog: FC<ApiKeyDialogProps> = ({onClose, onCreated}) => {
  const profileId = AppState.currentProfile?.id || '';
  const organizationId = AppState.currentProfile?.organization_id || '';

  const membersQuery = useOrganizationMembers(organizationId);
  const [submitting, setSubmitting] = useState(false);

  const profiles = (membersQuery.data || []).map((member) => ({label: `${member.first_name} ${member.last_name}`, value: member.id}));

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {name: '', profile_id: profileId, global_admin: false},
  });

  const globalAdmin = form.watch('global_admin');

  const onSubmit = (data: SchemaType) => {
    setSubmitting(true);
    return createApiKey(VerdocsEndpoint.getDefault(), data)
      .then((r) => {
        setSubmitting(false);
        toast.success('API key created');
        onCreated(r);
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error creating API key: ${e.message}`);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="overflow-y-auto">
        <div className="text-lg font-medium text-[#33364b]">Create an API key</div>
        <div className="mt-0.5 mb-4 text-[13px] text-gray-500">Keys authenticate server-to-server calls to the Verdocs API.</div>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TextInput
              label="Name"
              placeholder="Billing sync, staging"
              {...form.register('name')}
              required
              hint="Give the token a name to help you remember it by."
            />

            <div className="mb-1 text-[13px] font-medium text-[#33364b]">Access</div>
            <div className="mb-4 flex flex-col gap-2">
              <AccessCard
                selected={!globalAdmin}
                onSelect={() => form.setValue('global_admin', false, {shouldDirty: true})}
                icon={<UserIcon />}
                tone="personal"
                title="Personal"
                description="Acts as one person. It can see and change only what they can see and change."
              />
              <AccessCard
                selected={globalAdmin}
                onSelect={() => form.setValue('global_admin', true, {shouldDirty: true})}
                icon={<ShieldCheckIcon />}
                tone="admin"
                title="Global Admin"
                description="Full access to everything in the organization, including templates and envelopes that were never shared."
              />
            </div>

            {globalAdmin ? (
              <div className="mb-4 rounded-md border border-[#f0d9b5] bg-[#fdf8ef] p-3 text-[12px] text-[#95591a]">
                A global admin key overrides the permissions of whoever owns it, so it can read and change everything in the organization.
                Use it only where that is genuinely required.
              </div>
            ) : (
              <>
                <SelectInput
                  options={profiles}
                  name="profile_id"
                  label="Acts as"
                  hint="API calls made with this key will act as this person."
                />
              </>
            )}

            <div className="mt-6 flex flex-row items-center justify-end gap-3">
              {submitting && <Spinner mode="dark" />}
              <Button size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
              <Button size="small" label="Create key" type="submit" disabled={submitting} />
            </div>
          </form>
        </FormProvider>
      </div>
    </Dialog>
  );
};
